import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiService } from '../services/apiService';
import { useTestAuthFlow } from '../hooks/useTestAuthFlow';

export interface ContactSalesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  defaultEmail?: string;
  defaultName?: string;
}

type FormErrors = {
  name?: string;
  email?: string;
  companyName?: string;
  phoneNumber?: string;
  requirements?: string;
  captcha?: string;
};

const WORK_EMAIL_REGEX =
  /^[A-Za-z0-9](?:[A-Za-z0-9._+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

const isValidWorkEmail = (value: string): boolean => {
  const email = value.trim();
  if (!email) {
    return false;
  }
  if ((email.match(/@/g) || []).length !== 1 || email.includes('..')) {
    return false;
  }
  if (!WORK_EMAIL_REGEX.test(email)) {
    return false;
  }
  const [, domainPart] = email.split('@');
  const domainLabels = domainPart.split('.');
  if (domainLabels.some((label) => label.length < 1)) {
    return false;
  }
  const tld = domainLabels[domainLabels.length - 1];
  return tld.length >= 2 && /^[A-Za-z]+$/.test(tld);
};

export const isValidWorkEmailAddress = isValidWorkEmail;

const getWorkEmailError = (value: string): string | undefined => {
  const email = value.trim();
  if (!email) {
    return undefined;
  }
  if (isValidWorkEmail(email)) {
    return undefined;
  }
  if (!email.includes('@')) {
    return 'Please enter a valid email address (e.g. name@company.com)';
  }
  return 'Please enter a valid email address';
};

export const getWorkEmailValidationError = getWorkEmailError;
const PHONE_PATTERN = /^\d{7,15}$/;

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  defaultEmail = '',
  defaultName = '',
}) => {
  const { testAuthFlowEnabled, testStaticCaptcha } = useTestAuthFlow();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [requirements, setRequirements] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef<HTMLCanvasElement>(null);

  const drawCaptcha = useCallback((value: string) => {
    const canvas = captchaRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 120;
    canvas.height = 40;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = '20px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(value, canvas.width / 2, canvas.height / 2);

    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
  }, []);

  const generateCaptcha = useCallback(
    (clearCaptchaError = true) => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let code = '';
      for (let i = 0; i < 5; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setCaptchaText(code);
      setCaptchaInput('');
      if (clearCaptchaError) {
        setErrors((prev) => ({ ...prev, captcha: undefined }));
      }
      drawCaptcha(code);
    },
    [drawCaptcha]
  );

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEmail(defaultEmail);
      setCompanyName('');
      setPhoneNumber('');
      setRequirements('');
      setCaptchaInput('');
      setErrors({});
      setSubmitting(false);
      generateCaptcha();
    }
  }, [open, defaultEmail, defaultName, generateCaptcha]);

  useEffect(() => {
    if (open && captchaText) {
      drawCaptcha(captchaText);
    }
  }, [open, captchaText, drawCaptcha]);

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};
    if (!name.trim()) {
      nextErrors.name = 'Full name is required';
    }
    if (!email.trim()) {
      nextErrors.email = 'Work email is required';
    } else if (!isValidWorkEmail(email)) {
      nextErrors.email = 'Please enter a valid email address';
    }
    if (!companyName.trim()) {
      nextErrors.companyName = 'Company name is required';
    }
    if (!phoneNumber.trim()) {
      nextErrors.phoneNumber = 'Phone number is required';
    } else if (!PHONE_PATTERN.test(phoneNumber.trim())) {
      nextErrors.phoneNumber = 'Please enter a valid phone number';
    }
    if (!requirements.trim()) {
      nextErrors.requirements = 'Requirements are required';
    }
    if (!captchaInput.trim()) {
      nextErrors.captcha = 'Security code is required';
    } else if (
      captchaInput.trim() !== captchaText &&
      !(testAuthFlowEnabled && captchaInput.trim() === testStaticCaptcha)
    ) {
      nextErrors.captcha = 'Invalid security code (case-sensitive)';
    }
    return nextErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (validationErrors.captcha?.toLowerCase().includes('invalid')) {
        generateCaptcha(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      await apiService.submitContactSales({
        name: name.trim(),
        email: email.trim(),
        companyName: companyName.trim(),
        phoneNumber: phoneNumber.trim(),
        requirements: requirements.trim(),
      });
      onClose();
      onSuccess();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to submit your inquiry. Please try again.';
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      hideBackdrop
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: 520 },
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(15, 47, 125, 0.18)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: '#0f2f7d' }}>Contact Sales</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Full Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
            disabled={submitting}
          />
          <TextField
            label="Work Email"
            type="text"
            value={email}
            onChange={(e) => {
              let next = e.target.value.replace(/\s+/g, '');
              const atIndex = next.indexOf('@');
              if (atIndex !== -1) {
                const beforeAt = next.slice(0, atIndex + 1);
                const afterAt = next.slice(atIndex + 1).replace(/@/g, '');
                next = beforeAt + afterAt;
              }
              setEmail(next);
              setErrors((prev) => ({
                ...prev,
                email: getWorkEmailError(next),
              }));
            }}
            onBlur={() => {
              setErrors((prev) => ({
                ...prev,
                email: getWorkEmailError(email),
              }));
            }}
            error={!!errors.email}
            helperText={errors.email}
            required
            fullWidth
            disabled={submitting}
            inputProps={{
              inputMode: 'email',
              autoComplete: 'email',
            }}
          />
          <TextField
            label="Company Name"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              setErrors((prev) => ({ ...prev, companyName: undefined }));
            }}
            error={!!errors.companyName}
            helperText={errors.companyName}
            required
            fullWidth
            disabled={submitting}
          />
          <TextField
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value.replace(/\D/g, ''));
              setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
            }}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            required
            fullWidth
            disabled={submitting}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 15,
            }}
          />
          <TextField
            label="Requirements"
            value={requirements}
            onChange={(e) => {
              setRequirements(e.target.value);
              setErrors((prev) => ({ ...prev, requirements: undefined }));
            }}
            error={!!errors.requirements}
            helperText={errors.requirements}
            required
            fullWidth
            multiline
            minRows={4}
            disabled={submitting}
          />
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid #e8eaef',
              bgcolor: '#f8f9fc',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#495057' }}>
              Security verification
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Enter code"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value.replace(/\s+/g, ''));
                  setErrors((prev) => ({ ...prev, captcha: undefined }));
                }}
                disabled={submitting}
                error={!!errors.captcha}
                helperText={errors.captcha || 'Case-sensitive'}
                placeholder="e.g. Ab3xY"
                size="small"
                sx={{
                  flex: '1 1 140px',
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
                inputProps={{
                  maxLength: 5,
                  style: {
                    letterSpacing: '0.15em',
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                  },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Refresh security code" arrow>
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', cursor: 'pointer' }}
                    onClick={() => generateCaptcha()}
                  >
                    <canvas
                      ref={captchaRef}
                      style={{
                        border: '2px solid #dee2e6',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <IconButton
                    size="small"
                    onClick={() => generateCaptcha()}
                    disabled={submitting}
                    sx={{
                      color: '#0f2f7d',
                      bgcolor: 'rgba(15, 47, 125, 0.08)',
                      '&:hover': { bgcolor: '#0f2f7d', color: 'white' },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2f7d 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2347a8 0%, #133b95 100%)',
            },
          }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactSalesModal;
